import { regression } from 'echarts-stat';
import { DateTime } from 'luxon';

import ChartActionId from '@/common/enums/chart-action-id.enum';
import ChartGroupActionType from '@/common/enums/chart-group-action-type.enum';
import ChartTrendType from '@/common/enums/chart-trend-type.enum';
import TimeGranularity from '@/common/enums/time-granularity.enum';
import ChartAction from '@/common/models/chart/ChartAction';
import ChartGroupAction from '@/common/models/chart/ChartGroupAction';
import i18n from '@/vueI18n';

const getScaleByGranularity = (granularity: TimeGranularity): number => {
  switch (granularity) {
    case TimeGranularity.YEARLY:
      return 1;
    case TimeGranularity.MONTHLY:
      return 2;
    case TimeGranularity.DAILY:
      return 7;
    case TimeGranularity.HOURLY:
      return 12;
    default:
      return 0;
  }
};

const computeTrend = (xAxis: number[], yAxis: number[], scale: number, granularity: string): number[] => {
  const trendData: number[] = Array(scale);
  xAxis.forEach((x, i) => {
    const startIdx = i;
    const startDate = DateTime.fromMillis(x);
    const endIdx = xAxis.findIndex((d) => d >= startDate.plus({ [granularity]: scale }).toMillis());
    const pool = yAxis.slice(startIdx, endIdx);
    const sum = pool.reduce((a, b) => a + b, 0);
    const avg = sum / pool.length || 0;
    trendData.push(avg);
  });
  return trendData;
};

const regressionAnalysis = (regressionType: ChartTrendType, xAxis: number[], yAxis: { data: number[] }): number[] => {
  const dataset = xAxis.map((d, i) => [d, Number(yAxis.data[i])]);
  const regressionData = regression(
    regressionType as 'linear' | 'exponential' | 'logarithmic' | 'polynomial',
    dataset,
    0,
  );
  return regressionData.points.map((d) => d[1]);
};

export default class ChartTrendActionsUtils {
  static getChartTrendActions(rank: number, isHighlighted: boolean): ChartGroupAction {
    return new ChartGroupAction(
      ChartActionId.TREND,
      ChartGroupActionType.SELECTABLE,
      i18n.global.t('messages.trend'),
      rank,
      [
        new ChartAction(ChartTrendType.NONE, true, i18n.global.t('messages.showTrendFalse'), 0),
        new ChartAction(ChartTrendType.LINEAR, false, i18n.global.t('messages.showTrendLinear'), 1),
        new ChartAction(ChartTrendType.TREND, false, i18n.global.t('messages.showTrendTrue'), 2),
      ],
      isHighlighted,
      true,
    );
  }

  static get DefaultChartTrendActions(): ChartGroupAction {
    return ChartTrendActionsUtils.getChartTrendActions(2, false);
  }

  static getScaleByGranularity(granularity: TimeGranularity): number {
    return getScaleByGranularity(granularity);
  }

  static computeTrend(xAxis: number[], yAxis: number[], scale = 7, granularity = 'days'): number[] {
    return computeTrend(xAxis, yAxis, scale, granularity);
  }

  static regressionAnalysis(regressionType: ChartTrendType, xAxis: number[], yAxis: { data: number[] }): number[] {
    return regressionAnalysis(regressionType, xAxis, yAxis);
  }
}
